import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Images, Loader2, X, Cloud, Users, GitBranch } from 'lucide-react'
import Post from '../components/Post'
import RepostCard from '../components/RepostCard'
import CreatePostModal from '../components/CreatePostModal'
import api from '../services/api'

export default function Home() {
  const navigate = useNavigate()
  const [posts, setPosts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  const [feedMode, setFeedMode] = useState('recent')

  // Inline post creation state
  const [postText, setPostText] = useState('')
  const [mediaFile, setMediaFile] = useState<File | null>(null)
  const [mediaPreview, setMediaPreview] = useState<string | null>(null)
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null)
  const [isPosting, setIsPosting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Guest mode constants
  const GUEST_POST_LIMIT = 3

  // Autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [suggestionType, setSuggestionType] = useState<'mention' | 'hashtag' | null>(null)
  const [cursorPosition, setCursorPosition] = useState(0)

  const charLimit = 400

  // Mock data for autocomplete (replace with API calls later)
  const mockUsers = ['artist.ravi', 'john_doe', 'jane_smith', 'onrecord_official']
  const mockHashtags = ['breaking', 'news', 'onrecord', 'trending', 'journalism']

  useEffect(() => {
    checkAuthStatus()
    fetchPosts()
  }, [])

  // Home tab tap: scroll to top + refresh feed
  useEffect(() => {
    const handleHomeTap = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      fetchPosts()
    }
    window.addEventListener('home-tap', handleHomeTap)
    return () => window.removeEventListener('home-tap', handleHomeTap)
  }, [])

  const checkAuthStatus = async () => {
    const loggedIn = await api.isLoggedIn()
    setIsLoggedIn(loggedIn)
  }

  const fetchPosts = async () => {
    try {
      setLoading(true)
      const result = await api.getFeed({ limit: 20 })

      if (result.success && result.posts.length > 0) {
        // Normalize posts to match UI expectations
        const normalizedPosts = result.posts.map(post => ({
          id: post.originalPostId || post.postId,
          _feedKey: post.postId,
          authorId: post.author?.id || post.userId,
          author: post.author,
          content: post.content,
          mediaUrl: post.mediaUri,
          mediaType: post.mediaType,
          thumbnailUrl: post.thumbnailUri,
          createdAt: post.createdAt,
          location: post.location ? {
            ...post.location,
            address: post.location.city
              ? post.location.state
                ? `${post.location.city}, ${post.location.state}`
                : post.location.city
              : post.location.address || undefined,
          } : undefined,
          onRecordCode: post.onRecordCode,
          stats: post.stats,
          capturedAt: post.capturedAt || null,
          isRepost: post.isRepost || false,
          reposterName: post.reposterName,
          reposterUsername: post.reposterUsername,
        }))

        setPosts(normalizedPosts)
        console.log(`✅ Loaded ${normalizedPosts.length} posts from ${result.source}`)
      } else {
        console.warn('No posts returned from API')
        setPosts([])
      }
    } catch (error) {
      console.error('Error fetching posts:', error)
      setPosts([])
    } finally {
      setLoading(false)
    }
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value
    const cursorPos = e.target.selectionStart

    if (newText.length <= charLimit) {
      setPostText(newText)
      setCursorPosition(cursorPos)

      // Check for @ or # trigger for autocomplete
      const textBeforeCursor = newText.substring(0, cursorPos)
      const lastWord = textBeforeCursor.split(/\s/).pop() || ''

      if (lastWord.startsWith('@') && lastWord.length > 1) {
        // Mention autocomplete
        const query = lastWord.substring(1).toLowerCase()
        const filtered = mockUsers.filter(user =>
          user.toLowerCase().includes(query)
        )
        setSuggestions(filtered)
        setSuggestionType('mention')
        setShowSuggestions(filtered.length > 0)
      } else if (lastWord.startsWith('#') && lastWord.length > 1) {
        // Hashtag autocomplete
        const query = lastWord.substring(1).toLowerCase()
        const filtered = mockHashtags.filter(tag =>
          tag.toLowerCase().includes(query)
        )
        setSuggestions(filtered)
        setSuggestionType('hashtag')
        setShowSuggestions(filtered.length > 0)
      } else {
        setShowSuggestions(false)
      }
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    const textBeforeCursor = postText.substring(0, cursorPosition)
    const textAfterCursor = postText.substring(cursorPosition)
    const words = textBeforeCursor.split(/\s/)
    words[words.length - 1] = (suggestionType === 'mention' ? '@' : '#') + suggestion + ' '
    const newText = words.join(' ') + textAfterCursor

    setPostText(newText)
    setShowSuggestions(false)

    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus()
      const newCursorPos = words.join(' ').length
      textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }

  const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert('File size must be less than 50MB')
      return
    }

    // Validate file type
    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')

    if (!isImage && !isVideo) {
      alert('Please select an image or video file')
      return
    }

    setMediaFile(file)
    setMediaType(isImage ? 'image' : 'video')

    // Create preview
    const reader = new FileReader()
    reader.onload = (event) => {
      if (event.target?.result) {
        setMediaPreview(event.target.result as string)
      }
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveMedia = () => {
    setMediaFile(null)
    setMediaPreview(null)
    setMediaType(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleInlinePost = async () => {
    if (!postText.trim() && !mediaFile) {
      alert('Please add some text or media to your post')
      return
    }

    try {
      setIsPosting(true)

      const result = await api.createPost({
        text: postText.trim(),
        mediaFile: mediaFile || undefined,
        mediaType: mediaType || undefined,
      })

      if (result.success) {
        // Success! Reset form and refresh feed
        setPostText('')
        setMediaFile(null)
        setMediaPreview(null)
        setMediaType(null)
        fetchPosts()
      } else {
        alert(result.message || 'Failed to create post')
      }
    } catch (error) {
      console.error('Create post error:', error)
      alert('Failed to create post')
    } finally {
      setIsPosting(false)
    }
  }

  const canPost = (postText.trim() || mediaFile) && !isPosting

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 header-border z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-xl font-bold text-gray-900">Home</h1>
        </div>

        {/* Feed Mode Tabs */}
        <div className="feed-tabs-container">
          <div className="flex overflow-x-auto scrollbar-hide">
            {['recent', 'following'].map((mode) => (
              <button
                key={mode}
                onClick={() => setFeedMode(mode)}
                className="feed-tab"
              >
                <span className={feedMode === mode ? 'feed-tab-text-active' : 'feed-tab-text'}>
                  {mode === 'recent' ? 'For You' : 'Following'}
                </span>
                {feedMode === mode && <div className="feed-tab-underline" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Inline Post Creation - Only for logged in users */}
      {isLoggedIn && (
        <div className="p-4 border-b border-separator">
          <div className="flex gap-3 items-start">
            <div className="flex-1 relative">
              {/* Text Input */}
              <textarea
                ref={textareaRef}
                value={postText}
                onChange={handleTextChange}
                placeholder="go onrecord"
                className="w-full text-xl resize-none outline-none mb-2"
                style={{
                  background: 'transparent',
                  color: 'var(--smoke)'
                }}
                rows={1}
                disabled={isPosting}
              />

            {/* Autocomplete Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div
                className="absolute left-0 top-full mt-1 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10 w-64"
                style={{
                  background: 'var(--saturn-light)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-4 py-2 transition-colors flex items-center gap-2"
                    style={{ color: 'var(--smoke)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {suggestionType === 'mention' ? (
                      <>
                        <div className="w-8 h-8 rounded-full flex-shrink-0" style={{ background: 'rgba(255, 255, 255, 0.1)' }} />
                        <div>
                          <div className="font-medium text-sm">@{suggestion}</div>
                          <div className="text-xs" style={{ color: 'var(--silver)' }}>User</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="font-bold" style={{ color: 'var(--solar)' }}>#</span>
                        <span className="font-medium text-sm">{suggestion}</span>
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Media Preview - Only expands when media is added */}
            {mediaPreview && (
              <div className="relative mb-3 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                {mediaType === 'image' ? (
                  <img
                    src={mediaPreview}
                    alt="Upload preview"
                    className="w-full max-h-80 object-contain"
                    style={{ background: 'rgba(255, 255, 255, 0.03)' }}
                  />
                ) : (
                  <video
                    src={mediaPreview}
                    controls
                    className="w-full max-h-80 object-contain"
                    style={{ background: 'rgba(255, 255, 255, 0.03)' }}
                  />
                )}

                {/* Remove Media Button */}
                <button
                  onClick={handleRemoveMedia}
                  className="absolute top-2 right-2 p-1.5 rounded-full transition-colors"
                  style={{
                    background: 'rgba(13, 27, 42, 0.8)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(13, 27, 42, 0.95)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(13, 27, 42, 0.8)'}
                  disabled={isPosting}
                >
                  <X className="w-4 h-4" style={{ color: 'var(--smoke)' }} />
                </button>
              </div>
            )}

              {/* Actions Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  {/* Gallery */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 rounded-full transition-colors"
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--solar-glow)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    disabled={isPosting}
                    title="Gallery"
                  >
                    <Images className="w-5 h-5" style={{ color: 'var(--solar)' }} />
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*,video/*" onChange={handleMediaSelect} className="hidden" />

                  {/* Private Media */}
                  <button
                    onClick={() => navigate('/create')}
                    className="p-2 rounded-full transition-colors"
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--solar-glow)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    title="Private media"
                  >
                    <Cloud className="w-5 h-5" style={{ color: 'var(--solar)' }} />
                  </button>

                  {/* Shared With You */}
                  <button
                    onClick={() => navigate('/create')}
                    className="p-2 rounded-full transition-colors"
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--solar-glow)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    title="Shared with you"
                  >
                    <Users className="w-5 h-5" style={{ color: 'var(--solar)' }} />
                  </button>

                  {/* Thread */}
                  <button
                    onClick={() => navigate('/create-thread')}
                    className="p-2 rounded-full transition-colors"
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--solar-glow)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    title="Create thread"
                  >
                    <GitBranch className="w-5 h-5" style={{ color: 'var(--solar)' }} />
                  </button>

                  {/* Character Counter - Only show when typing */}
                  {postText.length > 0 && (
                    <span
                      className="text-sm ml-2"
                      style={{
                        color: postText.length > charLimit - 20
                          ? 'var(--alert)'
                          : postText.length > charLimit - 50
                          ? 'var(--solar)'
                          : 'var(--silver)'
                      }}
                    >
                      {postText.length}/{charLimit}
                    </span>
                  )}
                </div>

                {/* Post Button */}
                <button
                  onClick={handleInlinePost}
                  disabled={!canPost}
                  className="px-5 py-2 rounded-full font-bold transition-opacity"
                  style={{
                    background: canPost ? 'var(--solar)' : 'rgba(255, 255, 255, 0.1)',
                    color: canPost ? 'var(--saturn)' : 'var(--silver)',
                    cursor: canPost ? 'pointer' : 'not-allowed'
                  }}
                  onMouseEnter={(e) => { if (canPost) e.currentTarget.style.opacity = '0.9' }}
                  onMouseLeave={(e) => { if (canPost) e.currentTarget.style.opacity = '1' }}
                >
                  {isPosting ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Posting...</span>
                    </div>
                  ) : (
                    'Post'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Posts Feed */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12" style={{ borderBottom: '2px solid var(--solar)' }}></div>
        </div>
      ) : (
        <div>
          {/* Show limited posts for guests */}
          {(isLoggedIn ? posts : posts.slice(0, GUEST_POST_LIMIT)).map((post) => (
            post.isRepost ? (
              <RepostCard
                key={post._feedKey}
                reposterName={post.reposterName}
                reposterUsername={post.reposterUsername}
                post={post}
                isGuest={!isLoggedIn}
              />
            ) : (
              <Post key={post._feedKey || post.id} post={post} isGuest={!isLoggedIn} />
            )
          ))}

          {/* Login prompt for guests after 3 posts */}
          {!isLoggedIn && posts.length > GUEST_POST_LIMIT && (
            <div className="p-8" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <div className="max-w-md mx-auto text-center">
                <div className="mb-4">
                  <div
                    className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4"
                    style={{ background: 'var(--solar)' }}
                  >
                    <svg className="w-8 h-8" style={{ color: 'var(--saturn)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--smoke)' }}>
                    Join on record to see more
                  </h3>
                  <p className="mb-6" style={{ color: 'var(--silver)' }}>
                    Sign up to see more posts, interact with content, and share your own stories
                  </p>
                </div>

                <div className="space-y-3">
                  <a
                    href="/signup"
                    className="block w-full font-bold py-3 px-6 rounded-full transition-all text-white"
                    style={{
                      background: 'rgb(29, 155, 240)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgb(26, 140, 216)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgb(29, 155, 240)'}
                  >
                    Sign up
                  </a>
                  <a
                    href="/login"
                    className="block w-full font-bold py-3 px-6 rounded-full transition-all"
                    style={{
                      background: 'transparent',
                      color: 'rgb(29, 155, 240)',
                      border: '1px solid rgb(207, 217, 222)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(29, 155, 240, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    Log in
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Empty posts message */}
          {posts.length === 0 && !loading && (
            <div className="p-8 text-center text-gray-500">
              No posts yet. Be the first to post!
            </div>
          )}
        </div>
      )}

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </div>
  )
}
